import { Assessment, Close, Edit, Launch } from '@mui/icons-material';
import Card, { CardProps } from '../general/Card';
import React from 'react';
import WhiteFab from '../common/WhiteFab';
import { useFormik } from 'formik';
import { CA_REPORT, ReportStatusEnum } from './report.type';
import { TextField, Tooltip, Stack } from '@mui/material';
import DynamicDatePicker from '../common/DynamicDatePicker';
import { Button, Typography } from 'components/index';
import { TOOLTIPS } from './report.util';
import SelectInput from '../SelectInput';
import { Link } from 'react-router-dom';
import { ROUTES } from 'common/constants/urls';
import StyledTextField from '../project/StyledTextField';
import NumericFormatInput from '../general/NumericFormatInput';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { camelCaseToSpaceSeparated, capitalize, formatISODate } from 'common/utils/common.util';

type ReportDetailsCardProps = {
  report: CA_REPORT;
  updateReport: (updateFn: (prevReport: CA_REPORT) => CA_REPORT) => void;
  isLocked?: boolean;
  cardProps?: Partial<CardProps>;
  overheadAndProfitPercentage?: number | null;
};

const statusList = [...Object.values(ReportStatusEnum).map(status => ({ value: status, label: status }))];

const ReportDetailsCard: React.FC<ReportDetailsCardProps> = ({
  report,
  updateReport,
  isLocked = false,
  cardProps,
  overheadAndProfitPercentage,
}) => {
  const [isEditMode, setIsEditMode] = React.useState(false);
  const toggleEditMode = () => setIsEditMode(val => !val);

  // reset if props change
  React.useEffect(() => {
    if (isEditMode) setIsEditMode(false);
  }, [report?.status, isLocked]);

  const costThreshold = report?.generalInfo.costThreshold;
  const showIcon = !isEditMode && overheadAndProfitPercentage != null && costThreshold != null && costThreshold > 0;
  const isOverThreshold = showIcon && overheadAndProfitPercentage > costThreshold;

  const initialOrDefaultValues: Partial<CA_REPORT> = {
    status: report?.status,
    generalInfo: {
      ...report?.generalInfo,
      costThreshold: report?.generalInfo?.costThreshold,
    },
    budgetDated: report?.budgetDated ? new Date(report.budgetDated).toISOString().split('T')[0] : '',
    costDataFrom: report?.costDataFrom || '',
  };

  const formik = useFormik({
    initialValues: initialOrDefaultValues,
    enableReinitialize: true,
    onSubmit: values => {
      updateReport(report => ({
        ...report,
        status: values.status || '',
        costDataFrom: values.costDataFrom || '',
        budgetDated: values.budgetDated || null,
        generalInfo: {
          ...report.generalInfo,
          costThreshold: values.generalInfo?.costThreshold,
        },
      }));
      setIsEditMode(false);
    },
  });

  React.useEffect(() => {
    if (!formik.values.status) {
      formik.setFieldValue('status', ReportStatusEnum.InProgress);
    }
  }, [formik.values.status]);

  const canSubmit = React.useMemo(() => {
    if (isLocked) return false;
    if (!formik.isValid) return false;
    return formik.dirty;
  }, [formik, isLocked]);

  const handleCancel = () => {
    formik.resetForm();
    setIsEditMode(false);
  };

  return (
    <form onSubmit={formik.handleSubmit} style={{ height: '100%' }}>
      <Card
        itemsPerRow={cardProps?.itemsPerRow ?? 1}
        icon={cardProps?.icon ?? <Assessment color='primary' />}
        heading={cardProps?.heading ?? report.generalInfo.reportName ?? 'Report Details'}
        headerAction={
          isEditMode ? (
            <WhiteFab aria-label='cancel' icon={<Close fontSize='small' />} textColor='error' size='small' onClick={handleCancel} />
          ) : (
            <Tooltip
              title={
                isLocked && report.status === ReportStatusEnum.Completed ? TOOLTIPS.statusCompleted : isLocked ? TOOLTIPS.unlockToEdit : ''
              }
              arrow={false}
            >
              <span>
                <WhiteFab
                  aria-label='edit'
                  icon={<Edit fontSize='small' />}
                  textColor='primary'
                  size='small'
                  disabled={isLocked}
                  onClick={toggleEditMode}
                />
              </span>
            </Tooltip>
          )
        }
        footer={
          isEditMode ? (
            <Button disabled={!canSubmit} onClick={formik.submitForm} sx={{ marginLeft: 'auto' }}>
              Save Draft
            </Button>
          ) : undefined
        }
        data={{
          Project: (
            <Link
              style={{ textDecoration: 'none', color: 'unset', cursor: 'pointer' }}
              to={`${ROUTES.PROJECTS}/${report?.project?.projectId}`}
              target='_blank'
            >
              <Typography variant='body2' fontWeight='regular' gutterBottom>
                {report?.project?.projectTitle || '--'} <Launch sx={{ fontSize: '16px', ml: '5px' }} color='primary' />
              </Typography>
            </Link>
          ),
          Location: report?.project?.projectAddress
            ? report?.project?.projectAddress?.city === report?.project?.projectAddress?.postalCode &&
              report?.project?.projectAddress?.city != ''
              ? report?.project?.projectAddress?.city
              : report?.project?.projectAddress?.state
                ? [
                    report?.project?.projectAddress?.street,
                    capitalize(report?.project?.projectAddress?.city),
                    report?.project?.projectAddress?.state.toUpperCase(),
                    report?.project?.projectAddress?.postalCode,
                  ]
                    .filter(Boolean)
                    .join(', ')
                : report?.project?.projectAddress?.street || '--'
            : '--',
          'Project #': report?.project?.projectNumber || '--',
          'Cost Data': !isEditMode ? (
            report?.costDataFrom || report?.budgetDated ? (
              `Cost Data from ${report?.costDataFrom || '--'} - SOV dated ${formatISODate(report?.budgetDated || '', true) || '--'}`
            ) : (
              '--'
            )
          ) : (
            <TextField
              fullWidth
              size='small'
              variant='standard'
              name='costDataFrom'
              value={formik.values.costDataFrom}
              onChange={formik.handleChange}
              placeholder='Cost Data From'
            />
          ),
          ...(isEditMode
            ? {
                'Budget Dated': (
                  <DynamicDatePicker
                    fieldName='budgetDated'
                    value={formik.values.budgetDated || null}
                    onChange={(fieldName, value) => formik.setFieldValue(fieldName, value)}
                    textFieldProps={{ variant: 'standard', size: 'small' }}
                  />
                ),
              }
            : {}),
          Status: !isEditMode ? (
            camelCaseToSpaceSeparated(report.status || '--')
          ) : (
            <SelectInput
              selected={formik.values.status ?? ''}
              setSelected={(newVal: string | null) => {
                formik.setFieldValue('status', newVal === '' ? null : newVal);
              }}
              list={statusList}
              formatLabel={camelCaseToSpaceSeparated}
            />
          ),
          'Cost Threshold': !isEditMode ? (
            <Stack direction='row' alignItems='center' spacing={0.5}>
              <Typography variant='body2' fontWeight='regular'>
                {report?.generalInfo.costThreshold ? `${report.generalInfo.costThreshold}%` : '--'}
              </Typography>
              {showIcon && (
                <Tooltip title={`OH & P + General Conditions = ${overheadAndProfitPercentage.toFixed(2)}% of Report Budget`}>
                  {isOverThreshold ? (
                    <ErrorOutlineIcon color='error' sx={{ fontSize: 16 }} />
                  ) : (
                    <CheckCircleOutlineIcon color='success' sx={{ fontSize: 16 }} />
                  )}
                </Tooltip>
              )}
            </Stack>
          ) : (
            <StyledTextField
              name='generalInfo.costThreshold'
              variant='standard'
              value={formik.values.generalInfo?.costThreshold}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              InputProps={{
                inputComponent: NumericFormatInput as any,
                endAdornment: '%',
              }}
            />
          ),
        }}
      />
    </form>
  );
};

export default React.memo(ReportDetailsCard);
